import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { useUpdateCropSetting, UpdateCropSettingValidation, type CropSetting } from "@/lib/api/risk/profiles/cropSettings";
import { CropSettingsForm, type UpdateFormData } from "./CropSettingsForm";

interface UpdateCropSettingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  selectedSetting: CropSetting | null;
}

export function UpdateCropSettingDialog({ isOpen, onClose, onSuccess, selectedSetting }: UpdateCropSettingDialogProps) {
  const { toast } = useToast();
  const updateCropSetting = useUpdateCropSetting();
  const [isUpdating, setIsUpdating] = useState(false);

  const handleSuccess = () => {
    onSuccess();
    onClose();
  };

  const handleSubmit = async (data: UpdateFormData) => {
    if (!selectedSetting) return;

    setIsUpdating(true);
    try {
      // Validate data with Zod schema before sending to API
      const validationResult = UpdateCropSettingValidation.safeParse(data);

      if (!validationResult.success) {
        const errorMessages = validationResult.error.errors.map(err =>
          `${err.path.join('.')}: ${err.message}`
        ).join(', ');
        toast({
          title: "Invalid form data",
          description: errorMessages,
          variant: "destructive"
        });
        return;
      }

      const result = await updateCropSetting(selectedSetting.id, validationResult.data);

      if (!result.success) {
        throw result;
      }

      toast({ title: "Crop setting updated successfully" });
      handleSuccess();
    } catch (error) {
      console.error("Failed to update crop setting", error);
      toast({
        title: "Failed to update crop setting",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive"
      });
    } finally {
      setIsUpdating(false);
    }
  };

  if (!selectedSetting) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Update Crop Setting</DialogTitle>
          <DialogDescription>
            Update settings for {selectedSetting.commodity_name} ({selectedSetting.crop_year}).
          </DialogDescription>
        </DialogHeader>

        <div className="mb-4 p-3 bg-muted rounded-md">
          <div className="text-sm font-medium">Current Setting:</div>
          <div className="text-sm text-muted-foreground">
            {selectedSetting.commodity_name} ({selectedSetting.crop_year})
          </div>
        </div>

        <CropSettingsForm
          mode="update"
          initialData={selectedSetting}
          onSubmit={handleSubmit}
          onCancel={onClose}
          showCancelButton={true}
          isSubmitting={isUpdating}
        />
      </DialogContent>
    </Dialog>
  );
}
